import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import Cookie from "js-cookie";

import * as Constants from "../../../../constants/Constants";
import { formatDate } from "../../../../utils/DateUtility";
import {
  httpGetHierarchyByDivisionNumber,
  httpPostRequestDVNAuditApproval,
} from "../../../../service/httpClient";
import { getUserRolePermission } from "../../../../utils/rolePermissionAccess";
import * as rolePermConsts from "../../../../constants/RolePermissionConstants";
import DVNAuditModal from "./DVNAuditModal";

const getSortedArrayBasedOnDepartment = (hierarchyArr) => {
  return [
    ...hierarchyArr.sort((a, b) => {
      let firstDep = parseInt(a?.dept);
      let secDep = parseInt(b?.dept);

      if (firstDep && secDep) {
        return firstDep - secDep;
      }
      return;
    }),
  ];
};

const DVNAudit = ({ vendorDetails, getUpdatedVendorData }) => {
  const [auditData, setAuditData] = useState([]);
  const [isAuditable, setIsAuditable] = useState(false);
  const [departmentList, setDepartmentList] = useState({
    blm: [],
    mcy: [],
    blmOutlet: [],
    mcyBackstage: [],
  });
  const [openAuditCompleteModal, setAuditCompleteModal] = useState(false);
  const [approvalErrorMsg, setApprovalErrorMsg] = useState("");

  useEffect(() => {
    let hierarchyDetails = [
      ...vendorDetails.hierarchyDetails.map((item) => ({ ...item })),
    ];
    hierarchyDetails = getSortedArrayBasedOnDepartment(hierarchyDetails);
    setAuditData(hierarchyDetails);
  }, [vendorDetails.hierarchyDetails]);

  useEffect(() => {
    let hasAuditableDVN = false;
    if (auditData && auditData.length > 0) {
      for (let item of auditData) {
        if (
          item.needDvnAudit &&
          item.wasDvnAudited &&
          item.dvnRequestedBy !== Cookie.get("email") &&
          !item.auditApprovedByFullName &&
          !item.auditApprovedDate
        ) {
          hasAuditableDVN = true;
          break;
        }
      }
    }
    setIsAuditable(hasAuditableDVN);

    // Get department lists based on operating division
    getDepartmentsByOperatingDiv();
  }, [auditData]);

  const getDepartmentsByOperatingDiv = async () => {
    let departmentListData = { ...departmentList };
    let operatingDivs = new Set();
    auditData &&
      auditData.length > 0 &&
      auditData.forEach((item) => {
        operatingDivs.add(item.operatingDivision);
      });
    if (operatingDivs.size > 0) {
      let opsDiv = [];
      for (let item of operatingDivs) {
        switch (item) {
          case "BLOOMINGDALE'S":
            if (departmentList.blm.length === 0) {
              opsDiv.push(httpGetHierarchyByDivisionNumber("BLOOMINGDALE'S"));
            }
            break;
          case "BLOOMINGDALES OUTLET":
            if (departmentList.blmOutlet.length === 0) {
              opsDiv.push(
                httpGetHierarchyByDivisionNumber("BLOOMINGDALES OUTLET")
              );
            }
            break;
          case "MACY'S":
            if (departmentList.mcy.length === 0) {
              opsDiv.push(httpGetHierarchyByDivisionNumber("MACY'S"));
            }
            break;
          case "MACY'S BACKSTAGE":
            if (departmentList.mcyBackstage.length === 0) {
              opsDiv.push(httpGetHierarchyByDivisionNumber("MACY'S BACKSTAGE"));
            }
            break;
        }
      }

      if (opsDiv.length > 0) {
        try {
          const response = await Promise.all(opsDiv);
          if (response && response.length > 0) {
            for (let res of response) {
              if (res.status === 200) {
                let urlSegments = res.config?.url?.split("/") || [];
                if (urlSegments.length > 0) {
                  switch (urlSegments[urlSegments.length - 1]) {
                    case "BLOOMINGDALE'S":
                      departmentListData.blm = res.data;
                      break;
                    case "BLOOMINGDALES OUTLET":
                      departmentListData.blmOutlet = res.data;
                      break;
                    case "MACY'S":
                      departmentListData.mcy = res.data;
                      break;
                    case "MACY'S BACKSTAGE":
                      departmentListData.mcyBackstage = res.data;
                      break;
                  }
                }
              }
            }
          }
        } catch (e) {
          console.log(e);
        }
      }
    }
    setDepartmentList(departmentListData);
  };

  const handleChange = (e, index) => {
    setApprovalErrorMsg("");

    const { name } = e.target;
    const data = [...auditData.map((item) => ({ ...item }))];

    if (name === "wasDvnAudited") {
      data[index][name] = !data[index][name];
      data[index]["auditApprovedBy"] = data[index][name]
        ? Cookie.get("email").toLowerCase()
        : null;
    }
    setAuditData(data);
  };

  const getDepartmentName = (dept, operatingDivision) => {
    let deptList = [];
    switch (operatingDivision) {
      case "BLOOMINGDALE'S":
        deptList = departmentList.blm;
        break;
      case "BLOOMINGDALES OUTLET":
        deptList = departmentList.blmOutlet;
        break;
      case "MACY'S":
        deptList = departmentList.mcy;
        break;
      case "MACY'S BACKSTAGE":
        deptList = departmentList.mcyBackstage;
        break;
    }

    let deptObj = deptList.find((item) => item.deptNbr === parseInt(dept));
    return deptObj ? `${dept}-${deptObj.divnDeptDesc || ""}` : "";
  };

  const approveAuditComplete = () => {
    const data = {
      hierarchy: auditData.map(item => {
        return {
          ...item,
          auditApprovedByFullName: item.wasDvnAudited
            ? (
              item.auditApprovedDate ? 
              item.auditApprovedByFullName : Cookie.get("userName")
            )
            : null
        }
      }),
      userName: Cookie.get("userName"),
      userEmail: Cookie.get("email").toLowerCase(),
      duns: vendorDetails.duns,
      vendorProfileId: vendorDetails.id,
      brandName: vendorDetails.brandName,
    };

    httpPostRequestDVNAuditApproval(data)
      .then((res) => {
        if (res && res.status === 200) {
          getUpdatedVendorData(vendorDetails.id);
          setApprovalErrorMsg("");
          setAuditCompleteModal(false);
        } else {
          setApprovalErrorMsg("Problem in approving the DVN Audit");
        }
      })
      .catch((error) => {
        setApprovalErrorMsg(error.response?.data?.message);
      });
  };

  return (
    <>
      {getUserRolePermission(
        rolePermConsts.VENDORPLATFORM_PREBOARDING_DVNAUDIT,
        rolePermConsts.ALL
      ) !== rolePermConsts.NONE &&
        auditData &&
        auditData.length > 0 &&
        auditData.find(
          (item) =>
            item.needDvnAudit &&
            item.vendorNumber &&
            item.vendorNumber?.toString() !== "0"
        ) && (
          <Row className="dvn-audit-container">
            <DVNAuditModal
              show={openAuditCompleteModal}
              onModalClose={() => setAuditCompleteModal(false)}
              onApprove={approveAuditComplete}
              approvalErrorMsg={approvalErrorMsg}
            />
            <Col>
              <Row className="pt-0">
                <Col>
                  <Form autoComplete="none">
                    <Row className="px-0">
                      <Col lg={2} className="pr-0">
                        <Form.Label>Audit Approval</Form.Label>
                      </Col>
                      <Col lg={2} className="px-0">
                        <Form.Label>Operating Division</Form.Label>
                      </Col>
                      <Col lg={2} className="px-0">
                        <Form.Label>Department Number</Form.Label>
                      </Col>
                      <Col lg={2} className="px-0">
                        <Form.Label>Vendor Number</Form.Label>
                      </Col>
                      <Col lg={2} className="px-0">
                        <Form.Label>Audit Approver</Form.Label>
                      </Col>
                      <Col lg={2} className="px-0">
                        <Form.Label>Audit Date</Form.Label>
                      </Col>
                    </Row>
                    {auditData &&
                      auditData.length > 0 &&
                      auditData.map((item, i) => {
                        if (
                          !item.needDvnAudit ||
                          !item.vendorNumber ||
                          (item.vendorNumber &&
                            item.vendorNumber.toString() === "0")
                        ) {
                          return null;
                        }
                        return (
                          <Row className="p-0" key={`dvn-audit-${i}`}>
                            <Col lg={2} className="pr-0">
                              <Form.Group>
                                <Form.Check
                                  id={`wasDvnAudited-${i}`}
                                  name="wasDvnAudited"
                                  type="checkbox"
                                  onChange={(e) => handleChange(e, i)}
                                  checked={item.wasDvnAudited}
                                  disabled={
                                    getUserRolePermission(
                                      rolePermConsts.VENDORPLATFORM_PREBOARDING_DVNAUDIT,
                                      rolePermConsts.ALL
                                    ) !== rolePermConsts.EDIT ||
                                    (item.wasDvnAudited &&
                                      item.auditApprovedBy &&
                                      item.auditApprovedDate) ||
                                    item.dvnRequestedBy === Cookie.get("email")
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={2} className="px-0 pr-2">
                              <Form.Group>
                                <Form.Control
                                  id={`operatingDivision-${i}`}
                                  name="operatingDivision"
                                  value={item.operatingDivision}
                                  onChange={(e) => {}}
                                  disabled
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={2} className="px-0 pr-2">
                              <Form.Group>
                                <Form.Control
                                  id={`dept-${i}`}
                                  name="dept"
                                  value={getDepartmentName(
                                    item.dept,
                                    item.operatingDivision
                                  )}
                                  onChange={(e) => {}}
                                  disabled
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={2} className="px-0 pr-2">
                              <Form.Group>
                                <Form.Control
                                  id={`vendorNumber-${i}`}
                                  name="vendorNumber"
                                  value={item.vendorNumber}
                                  onChange={(e) => {}}
                                  disabled
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={2} className="px-0 pr-2">
                              <Form.Group>
                                <Form.Control
                                  id={`auditApprovedByFullName-${i}`}
                                  name="auditApprovedByFullName"
                                  value={item.auditApprovedByFullName}
                                  onChange={(e) => {}}
                                  disabled
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={2} className="px-0 pr-2">
                              <Form.Group>
                                <Form.Control
                                  id={`auditApprovedDate-${i}`}
                                  name="auditApprovedDate"
                                  value={
                                    item.auditApprovedDate
                                      ? formatDate(item.auditApprovedDate)
                                      : ""
                                  }
                                  onChange={(e) => {}}
                                  disabled
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        );
                      })}
                  </Form>
                </Col>
              </Row>
              <Row>
                <div className="ml-auto pr-2">
                  <Button
                    disabled={
                      getUserRolePermission(
                        rolePermConsts.VENDORPLATFORM_PREBOARDING_DVNAUDIT,
                        rolePermConsts.ALL
                      ) !== rolePermConsts.EDIT || !isAuditable
                    }
                    className="button-primary-md button-primary-auto"
                    onClick={() => setAuditCompleteModal(true)}
                  >
                    {Constants.BUTTON_AUDIT_COMPLETE}
                  </Button>
                </div>
              </Row>
            </Col>
          </Row>
        )}
    </>
  );
};

export default DVNAudit;
